import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as rolesApi from './api'
import type { CreateRoleRequest, UpdateRoleRequest } from './types'

export const roleKeys = {
  list: () => ['access', 'roles'] as const,
  allPermissions: () => ['access', 'permissions'] as const,
  permissions: (code: string) => ['access', 'roles', code, 'permissions'] as const,
}

export function useRoles() {
  return useQuery({
    queryKey: roleKeys.list(),
    queryFn: rolesApi.listRoles,
  })
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: roleKeys.list() })
}

export function useCreateRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateRoleRequest) => rolesApi.createRole(payload),
    onSuccess: () => invalidate(qc),
  })
}

export function useUpdateRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { code: string; payload: UpdateRoleRequest }) =>
      rolesApi.updateRole(vars.code, vars.payload),
    onSuccess: () => invalidate(qc),
  })
}

export function useDeleteRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (code: string) => rolesApi.deleteRole(code),
    onSuccess: () => invalidate(qc),
  })
}

export function useAllPermissions() {
  return useQuery({
    queryKey: roleKeys.allPermissions(),
    queryFn: rolesApi.getAllPermissions,
  })
}

export function useRolePermissions(code: string) {
  return useQuery({
    queryKey: roleKeys.permissions(code),
    queryFn: () => rolesApi.getRolePermissions(code),
    enabled: !!code,
  })
}

export function useSetRolePermissions(code: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (codes: string[]) => rolesApi.setRolePermissions(code, codes),
    onSuccess: () => qc.invalidateQueries({ queryKey: roleKeys.permissions(code) }),
  })
}
